import React, { useRef } from 'react'
import clsx from 'clsx'
import { isEmpty, isNil } from 'ramda'
import moment from 'moment'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { Typography, makeStyles } from '@material-ui/core'

import { ClipBoardIcon, VoucherIcon } from 'app/components/icons'
import { ReactComponent as TableNotFoundIcon } from 'app/components/icons/table-not-found-icon.svg'

import Topic from 'app/components/Topic'
import { CARD_VIEW, DRAFT } from 'app/constants/messageDetail'
import { getSettingByKey, useAppSettings } from 'commons/useAppSettings'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'
import {
  checkPoll,
  checkEvent,
  checkDraft,
  checkUnsend,
  checkMetrics,
  checkPublicLink,
} from 'commons/timelineAction'

import Channels from 'app/componentV4/Channel/Channels'
import Recipients from 'app/componentV4/MessageDetail/Recipients'
import MessageType from 'app/componentV4/MessageDetail/MessageType'
import EventBadge from 'app/componentV4/MessageDetail/Event/EventBadge'
import EventDetail from 'app/componentV4/MessageDetail/Event/EventDetail'
import TextToSpeech from 'app/componentV4/MessageDetail/TextToSpeech/TextToSpeech'
import SimplePollDetails from 'app/componentV4/MessageDetail/Poll/SimplePollDetails'
import PollListOnTimeline from 'app/componentV4/MessageDetail/Poll/PollListOnTimeline'

import Actions from 'app/main/messages/timeline/Action/Actions'
import Status from 'app/main/messages/timeline/components/Status'
import Badges from 'app/main/messages/timeline/components/Badges'
import PreviewBody from 'app/main/messages/timeline/components/PreviewBody'
import LoaderCardView from 'app/main/messages/timeline/Skeleton/LoaderCardView'
import { getLocalizedDate } from 'commons/date'
import { getSubGroupOptions } from 'commons/groupLogic'

const useStyles = makeStyles((theme) => ({
  recipient: {
    '& .recipient-tooltip': {
      color: `${theme.colors.primary} !important`,
      fontSize: '14px !important',
      fontWeight: 'bold !important',
    },
  },
  preview: {
    '& .preview-image': {
      width: 'fit-content',
      maxWidth: '450px',
      height: 'fit-content',
      maxHeight: '300px',
      margin: 'auto',
    },
  },
}))

const CardView = (props) => {
  const {
    updateId,
    isLoading,
    isUpdating,
    onOpenDetail,
    onOpenMetric,
    notifications,
    isOpenDetail,
    onSuccseAndRedirectPage,
    handleFetchTimeline,
    onOpenComment = () => {},
    isFavoriteLoading,
    updateFavorites,
  } = props

  const { formatMessage } = useIntl()
  const classes = useStyles()
  const cardRef = useRef(null)
  const tabView = Number(localStorage.getItem('timelineView')) ?? 0

  const currentMusicPlaying = useRef([])

  const isHideTtsHistory = Number(
    getSettingByKey('hide_tts_history')(useAppSettings()),
  )

  const handleShowTTS = (isTTSEnabled, isDraft) => {
    return isTTSEnabled && !isDraft && isHideTtsHistory !== 1
  }

  const handleSelectRow = (data) => {
    if (data.status === DRAFT) {
      // handlerEditCompose(data.id, recipients)
    } else {
      onOpenDetail(data.id)
    }
  }

  const renderType = (type, isReminder) => {
    switch (type) {
      case TYPE_GENERAL:
        return (
          <div>
            <Typography className="text-14 font-normal text-primary-300">
              <FormattedMessage id="notification.type.general" />
            </Typography>
            {isReminder ? (
              <Typography className="text-12 font-normal text-primary-300">
                <FormattedMessage id="text.reminder" />
              </Typography>
            ) : null}
          </div>
        )
      case TYPE_EVENT:
        return (
          <Typography className="text-14 font-normal text-primary-300">
            <FormattedMessage id="notification.type.event" />
          </Typography>
        )
      case TYPE_POLL:
        return (
          <Typography className="text-14 font-normal text-primary-300">
            <FormattedMessage id="notification.type.poll" />
          </Typography>
        )
      default:
        return
    }
  }

  return (
    <div className="space-y-10">
      {isLoading ? (
        <LoaderCardView rowsNum={5} />
      ) : (
        <>
          {isEmpty(notifications) ? (
            <div className="border-0">
              <div className="mx-auto flex mt-136 w-fit">
                <TableNotFoundIcon />
              </div>
              <Typography className="text-16 font-bold text-neutrals-800 my-5 flex justify-center">
                <FormattedMessage id="text.no.results" />
              </Typography>
              <Typography className="text-neutrals-400 my-2 flex justify-center">
                <FormattedMessage id="text.adjusting.search" />
              </Typography>
            </div>
          ) : (
            <>
              {notifications.map((notification) => {
                const formattedGroups = getSubGroupOptions(
                  formatMessage,
                  notification.groups,
                )
                return (
                  <div key={`message-card-view-${notification.id}`}>
                    {isUpdating && updateId === notification.id ? (
                      <LoaderCardView rowsNum={1} />
                    ) : (
                      <div
                        ref={cardRef}
                        className={'bg-white rounded-lg shadow p-10 max-w-full'}
                      >
                        {/* Type and Action */}
                        <div className="w-full flex justify-between">
                          <div className="flex space-x-3 items-center">
                            <MessageType
                              type={notification.type}
                              isReminder={notification.isReminder}
                              fill="#5D7290"
                            />
                            {renderType(
                              notification.type,
                              notification.isReminder,
                            )}
                            <Status status={notification.status} />
                          </div>

                          <Actions
                            id={notification.id}
                            isFavorite={notification.isFavorited || false}
                            isDisplayFavorite={true}
                            isPoll={checkPoll(notification.poll)}
                            isPublicLink={checkPublicLink(
                              notification.publicLink,
                              notification.status,
                            )}
                            isSharingEnabled={notification.isSharingEnabled}
                            isUnsend={checkUnsend(
                              notification.type,
                              notification.status,
                              notification.channels,
                              notification?.event?.status,
                            )}
                            isDraft={checkDraft(notification.status)}
                            isDialog={() => {
                              return
                            }}
                            isMetrics={checkMetrics(
                              notification.isMetricDisabled,
                              notification.status,
                              notification.metricDeliveryPercentage,
                            )}
                            isEvent={checkEvent(
                              notification.type,
                              notification.status,
                              notification?.event?.status,
                              isNil(notification.scheduleCanceledDate),
                            )}
                            isScheduled={
                              notification.isScheduled &&
                              isNil(notification.scheduleCanceledDate)
                            }
                            handleFetchTimeline={handleFetchTimeline}
                            onSuccseAndRedirectPage={onSuccseAndRedirectPage}
                            onOpenMetric={() => onOpenMetric(notification.id)}
                            scheduleCanceledDate={
                              notification.scheduleCanceledDate
                            }
                            isReminder={
                              notification.isReminder &&
                              !isNil(notification.reminder)
                            }
                            onOpenComment={() => onOpenComment(notification.id)}
                            countComment={notification.comments}
                            isFavoriteLoading={isFavoriteLoading}
                            updateFavorites={updateFavorites}
                          />
                        </div>
                        {/* Information */}
                        <div
                          className="cursor-pointer"
                          onClick={() => handleSelectRow(notification)}
                        >
                          <div className="w-full flex justify-between">
                            <div className="my-5 space-y-3">
                              <div className="flex divide-x divide-neutrals-200">
                                <div className="flex pr-3">
                                  <Typography className="flex text-14 font-normal text-primary">
                                    <FormattedMessage id="timeline.detail.message.to" />
                                  </Typography>
                                  &nbsp;
                                  <Recipients
                                    groups={formattedGroups}
                                    recipients={notification.recipients}
                                    channels={notification.channels}
                                    isDraft={checkDraft(notification.status)}
                                    isScheduled={notification.isScheduled}
                                    isTooltip={true}
                                    className={classes.recipient}
                                  />
                                </div>

                                <Typography className="whitespace-nowrap text-12 text-grey-200 font-normal px-3">
                                  {getLocalizedDate(
                                    notification.createdAt,
                                    'DATE_TIME',
                                  )}
                                </Typography>

                                <div className="px-3">
                                  <Channels
                                    channels={notification.channels}
                                    isTooltip={true}
                                  />
                                </div>

                                <Typography className="whitespace-nowrap text-12 text-grey-200 font-normal px-3">
                                  <FormattedMessage
                                    id="created.by.label"
                                    values={{
                                      fullName: (
                                        <span
                                          className={
                                            !isNil(
                                              notification.creator.deletedAt,
                                            )
                                              ? 'line-through'
                                              : ''
                                          }
                                        >
                                          {`${notification.creator.firstName} ${notification.creator.lastName}`}
                                        </span>
                                      ),
                                    }}
                                  />
                                </Typography>
                              </div>

                              {isNil(notification.event) ? null : (
                                <EventDetail
                                  id={notification.id}
                                  data={notification.event}
                                  isPropose={false}
                                  isEventCanceled={false}
                                />
                              )}
                              {isNil(notification.poll) ? null : (
                                <SimplePollDetails data={notification.poll} />
                              )}
                            </div>
                            {notification.isDraft ? null : (
                              <div className="my-5 space-x-3">
                                {/* isScheduled */}
                                {notification.isScheduled ? (
                                  <Badges
                                    isCanceleScheduled={
                                      notification.isScheduled &&
                                      !isNil(notification.scheduleCanceledDate)
                                    }
                                    date={notification.scheduleDate}
                                  />
                                ) : null}
                                {/* Event status */}
                                {isNil(notification.event) ? null : (
                                  <EventBadge
                                    status={notification.event.status}
                                  />
                                )}
                                {/* expirationDate */}
                                {notification.expirationDate ? (
                                  <Badges
                                    isExpiration={true}
                                    date={notification.expirationDate}
                                  />
                                ) : null}
                                {/* poll */}
                                {isNil(notification.poll) ? null : (
                                  <Badges
                                    isPoll={true}
                                    poll={notification.poll}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          {/* Title Body */}
                          <div className="py-5 border-t-1 border-neutrals-200">
                            <PreviewBody
                              data={notification}
                              className={clsx(classes.preview, 'space-y-5')}
                            />
                          </div>
                          {isNil(notification.poll) ? null : (
                            <PollListOnTimeline
                              data={notification.poll.options}
                              total={notification.poll.optionCount}
                            />
                          )}
                        </div>
                        {isEmpty(notification.topics) ? null : (
                          <Typography
                            component="p"
                            className="truncate text-sky-600 my-5"
                          >
                            {notification.topics.map((topic, index) => (
                              <Topic
                                id={index}
                                key={index}
                                name={topic}
                                style={{
                                  icon: 'max-w-16 h-auto',
                                  text: 'text-14 mr-3',
                                }}
                              />
                            ))}
                          </Typography>
                        )}
                        {handleShowTTS(
                          notification.isTTSEnabled,
                          notification.isDraft,
                        ) ? (
                          <TextToSpeech
                            id={notification.id}
                            isLoadingAudio={false}
                            isStopAudio={isOpenDetail || tabView !== CARD_VIEW}
                            audioUrl={notification.audioUrl}
                            currentMusicPlaying={currentMusicPlaying.current}
                            languageId={notification.translation.languageId}
                          />
                        ) : null}
                        <div className="flex space-x-5">
                          {isNil(notification.defaultImageUrl) ? null : (
                            <div className="flex space-x-3 items-center">
                              <ClipBoardIcon />
                              <Typography className="text-12 text-primary-300 font-normal">
                                <FormattedMessage id="label.file.attched" />
                              </Typography>
                            </div>
                          )}
                          {notification.isVoucher ? (
                            <div className="flex space-x-3 items-center">
                              <VoucherIcon />
                              <Typography className="text-12 text-primary-300 font-normal">
                                <FormattedMessage id="label.voucher.attched" />
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </>
      )}
    </div>
  )
}

const dataPropTypes = {
  id: PropTypes.number,
  type: PropTypes.number,
  scope: PropTypes.number,
  level: PropTypes.number,
  isScheduled: PropTypes.bool,
  isDraft: PropTypes.bool,
  isCanceled: PropTypes.bool,
  isReminder: PropTypes.bool,
  isTTSEnabled: PropTypes.bool,
  isSharingEnabled: PropTypes.bool,
  isMetricDisabled: PropTypes.bool,
  metricDeliveryPercentage: PropTypes.number,
  timezone: PropTypes.string,
  subUserId: PropTypes.number,
  channels: PropTypes.arrayOf(PropTypes.number),
  translation: PropTypes.shape({
    title: PropTypes.string,
    descriptionPreview: PropTypes.string,
    body: PropTypes.string,
    languageId: PropTypes.number,
    languageCode: PropTypes.string,
  }),
  audioUrl: PropTypes.string,
  publicLink: PropTypes.string,
  status: PropTypes.string,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  sendDate: PropTypes.string,
  unsendDate: PropTypes.string,
  scheduleCanceledDate: PropTypes.string,
  expirationDate: PropTypes.string,
  canceledDate: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  defaultImageType: PropTypes.string,
  event: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTimeZone: PropTypes.string,
    endTimeZone: PropTypes.string,
    status: PropTypes.number,
  }),
  createdAt: PropTypes.string,
  createdBy: PropTypes.string,
  updatedAt: PropTypes.string,
  creator: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    deletedAt: PropTypes.string,
  }),
  topics: PropTypes.arrayOf(PropTypes.string),
  poll: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    status: PropTypes.string,
    isAnonymous: PropTypes.bool,
    endDate: PropTypes.string,
    endDateTimeZone: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    optionCount: PropTypes.number,
  }),
}
CardView.propTypes = {
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isFavoriteLoading: PropTypes.bool,
  updateId: PropTypes.number,
  recipients: PropTypes.object,
  onOpenDetail: PropTypes.func,
  onOpenMetric: PropTypes.func,
  isOpenDetail: PropTypes.bool,
  onOpenComment: PropTypes.func,
  handleFetchTimeline: PropTypes.func,
  onSuccseAndRedirectPage: PropTypes.func,
  notifications: PropTypes.arrayOf(PropTypes.shape(dataPropTypes)).isRequired,
  updateFavorites: PropTypes.func,
}

export default CardView
